import GlobalBlackFridayBanner from '@/shared/blackFriday/GlobalBlackFridayBanner';
import DashboardSkeleton from '@/shared/loaders/DashboardSkeleton';
import RouteSkeleton from '@/shared/loaders/RouteSkeleton';
import ToastMessage from '@/shared/global/ToastMessage';
import { Frame } from '@shopify/polaris';
import { PATHS } from 'config/constants';
import { useToast } from 'hooks/useToast';
import { useUpgradeModalShow } from 'hooks/useUpgradeModalShow';
import React, { Suspense, lazy, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import { dismissToast } from 'states/actions/toast';

const HomeNew = lazy(() => import('./pages/dashboard/Dashboard'));

// Lazy on purpose: statically imported, UpgradeModal drags the whole pricingV2
// <Plans /> purchase tree into the router (= main) bundle, and it only ever
// renders after a merchant clicks an upgrade affordance — never on first
// paint. Same optimization Discount Prime made in its CWV overhaul. No
// Suspense fallback needed beyond null: it's a modal overlay, so a late mount
// shifts nothing.
const UpgradeModal = lazy(() => import('@/shared/UpgradeModal'));

const PricingV2 = lazy(() => import('./pages/pricingV2/Pricing'));

const Help = lazy(() => import('./pages/help/Help'));
const Setting = lazy(() => import('./pages/setting/Setting'));
const Design = lazy(() => import('./pages/design/Design'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Login = lazy(() => import('./pages/Login'));
const Rules = lazy(() => import('./pages/rules/Rules'));
const SelectRuleType = lazy(() => import('./pages/rules/SelectRuleType'));
const RulePage = lazy(() => import('./pages/rules/Rule'));
const RuleOverlaps = lazy(() => import('./pages/rules/Overlaps'));
const CheckProduct = lazy(() => import('./pages/rules/CheckProduct'));
const SafeUninstall = lazy(() => import('./pages/safeUninstall/SafeUninstall'));
const Automations = lazy(() => import('./pages/automations/Automations'));

const Router = () => {
  const dispatch = useDispatch();
  const upgradeModalShow = useUpgradeModalShow();

  const toast = useToast();
  const handleDismissToast = useCallback(() => {
    dismissToast(dispatch);
  }, [dispatch]);

  // The Suspense fallback below was `<div></div>` — zero height, so every route
  // swap replaced nothing with a full page and booked a large layout shift,
  // which is exactly what the Built for Shopify CLS budget measures.
  // RouteSkeleton reserves roughly the real page's box instead.
  return (
    <Suspense fallback={<RouteSkeleton />}>
      <Frame>
        <GlobalBlackFridayBanner />

        <Routes>
          <Route path={PATHS.PRICING} element={<PricingV2 />} />

          {/* Own boundary with a hint-aware skeleton: re-entering the app from
              an admin menu reboots the iframe, so the home route replays
              skeleton→real on every visit — this fallback reserves the exact
              boxes (review banner, Get-started card) the real first paint will
              occupy, so nothing jumps. Bonus: suspending inside the Routes
              keeps Frame + nav mounted instead of swapping the whole shell. */}
          <Route
            path={PATHS.HOME}
            element={
              <Suspense fallback={<DashboardSkeleton />}>
                <HomeNew />
              </Suspense>
            }
          />

          <Route path={`${PATHS.HELP}`} element={<Help />} />
          <Route path={`${PATHS.HELP}/:tab`} element={<Help />} />
          <Route path={PATHS.SETTING} element={<Setting />} />
          {/* Preferences merged into Settings — redirect any existing link/bookmark. */}
          <Route path={PATHS.PREFERENCES} element={<Navigate to={PATHS.SETTING} replace />} />
          <Route path={PATHS.DESIGN} element={<Design />} />

          <Route path={PATHS.RULES} element={<Rules />} />
          <Route path={PATHS.SELECT_RULE_TYPE} element={<SelectRuleType />} />
          <Route path={`${PATHS.CREATE_RULE}`} element={<RulePage />} />
          <Route path={`${PATHS.EDIT_RULE}/:ruleId`} element={<RulePage />} />
          <Route path={PATHS.RULES_OVERLAPS} element={<RuleOverlaps />} />
          <Route path={PATHS.RULES_CHECK_PRODUCT} element={<CheckProduct />} />

          <Route path={PATHS.SAFE_UNINSTALL} element={<SafeUninstall />} />
          <Route path={PATHS.AUTOMATIONS} element={<Automations />} />
          {/* Alerts + Integrations merged into Settings, Reports merged into
              the Dashboard — redirect any existing link/bookmark. */}
          <Route path={PATHS.ALERTS} element={<Navigate to={PATHS.SETTING} replace />} />
          <Route path={PATHS.INTEGRATIONS} element={<Navigate to={PATHS.SETTING} replace />} />
          <Route path={PATHS.REPORTS} element={<Navigate to={PATHS.HOME} replace />} />
          <Route path={PATHS.LOGIN} element={<Login />} />
          <Route element={<NotFound />} />
        </Routes>

        {toast?.show && <ToastMessage handleDismiss={handleDismissToast} {...toast} />}
        {/* Own Suspense boundary with a null fallback — without it, the modal
            chunk loading would trip the route-level Suspense above and swap
            the whole visible page for RouteSkeleton mid-interaction. */}
        {upgradeModalShow && (
          <Suspense fallback={null}>
            <UpgradeModal />
          </Suspense>
        )}
      </Frame>
    </Suspense>
  );
};

export default Router;
